import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import {
    buildPolarGridCellsSvg,
    CELL_OBJECT_TYPES,
    DEFAULT_POLAR_GRID_CELLS_CONFIG,
    type CellObjectType,
    type PolarGridCellsConfig,
    type PolarObjectSvg,
    type RingConfig,
    type RingPopulationRule,
    type ScalarMode,
    type ShapeMode,
    normalizeRingConfigs,
} from "../../utils/polarGridCells";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import { isValidSvg } from "../../utils/svgUtils";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

// ─── Re-exports for controls ──────────────────────────────────────────────────

export type {
    CellObjectType,
    ShapeMode,
    ScalarMode,
    RingConfig,
    RingPopulationRule,
    PolarObjectSvg,
};

// ─── Context interface ────────────────────────────────────────────────────────

interface PolarGridCellsState {
    // grid geometry
    rings: number;
    ringConfigs: RingConfig[];
    innerRadiusFraction: number;
    cellPadding: number;
    canvasSize: number;

    // shapes
    shapeMode: ShapeMode;
    uniformObjectSvgIndex: number;
    perRingObjectSvgIndices: number[];
    perSectorObjectSvgIndices: number[];
    uniformShape: CellObjectType;
    perRingShapes: CellObjectType[];
    perSectorShapes: CellObjectType[];

    // size
    sizeMode: ScalarMode;
    uniformSize: number;
    sizeMin: number;
    sizeMax: number;

    // opacity
    opacityMode: ScalarMode;
    uniformOpacity: number;
    opacityMin: number;
    opacityMax: number;

    // style
    fill: string;
    rotateWithCell: boolean;

    // custom SVG
    useCustomSvg: boolean;
    svgSymbol: string;
    objectSvgs: PolarObjectSvg[];

    seed: number;

    // derived
    svgContent: string;

    // setters
    setRings: (v: number) => void;
    setRingSectors: (ringIdx: number, sectors: number) => void;
    setRingRotationDeg: (ringIdx: number, rotationDeg: number) => void;
    setRingHeightWeight: (ringIdx: number, heightWeight: number) => void;
    setRingCellHeightFactor: (ringIdx: number, factor: number) => void;
    setRingPopulationRule: (ringIdx: number, rule: RingPopulationRule) => void;
    setInnerRadiusFraction: (v: number) => void;
    setCellPadding: (v: number) => void;
    setCanvasSize: (v: number) => void;
    setShapeMode: (v: ShapeMode) => void;
    setUniformObjectSvgIndex: (v: number) => void;
    setPerRingObjectSvgIndex: (ringIdx: number, idx: number) => void;
    setPerSectorObjectSvgIndex: (sectorIdx: number, idx: number) => void;
    setUniformShape: (v: CellObjectType) => void;
    setPerRingShape: (ringIdx: number, shape: CellObjectType) => void;
    setPerSectorShape: (sectorIdx: number, shape: CellObjectType) => void;
    setSizeMode: (v: ScalarMode) => void;
    setUniformSize: (v: number) => void;
    setSizeMin: (v: number) => void;
    setSizeMax: (v: number) => void;
    setOpacityMode: (v: ScalarMode) => void;
    setUniformOpacity: (v: number) => void;
    setOpacityMin: (v: number) => void;
    setOpacityMax: (v: number) => void;
    setFill: (v: string) => void;
    setRotateWithCell: (v: boolean) => void;
    setUseCustomSvg: (v: boolean) => void;
    setSvgSymbol: (v: string) => void;
    addObjectSvg: (svg: PolarObjectSvg) => void;
    removeObjectSvg: (index: number) => void;
    setRingObjectSvgIndex: (ringIdx: number, objectIdx: number) => void;
    setSeed: (v: number) => void;

    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    randomizeParameters: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const PolarGridCellsContext = createContext<PolarGridCellsState | null>(null);

export function usePolarGridCells(): PolarGridCellsState {
    const ctx = useContext(PolarGridCellsContext);
    if (!ctx)
        throw new Error(
            "usePolarGridCells must be used within PolarGridCellsProvider",
        );
    return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

const D = DEFAULT_POLAR_GRID_CELLS_CONFIG;

export function PolarGridCellsProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [rings, setRingsState] = useState(D.rings);
    const [sectors] = useState(D.sectors);
    const [ringConfigs, setRingConfigs] = useState<RingConfig[]>(
        normalizeRingConfigs(D.rings, D.ringConfigs, D.sectors),
    );
    const [innerRadiusFraction, setInnerRadiusFraction] = useState(
        D.innerRadiusFraction,
    );
    const [cellPadding, setCellPadding] = useState(D.cellPadding);
    const [canvasSize, setCanvasSize] = useState(D.canvasSize);

    const [shapeMode, setShapeMode] = useState<ShapeMode>(D.shapeMode);
    const [uniformObjectSvgIndex, setUniformObjectSvgIndex] = useState(
        D.uniformObjectSvgIndex,
    );
    const [perRingObjectSvgIndices, setPerRingObjectSvgIndices] = useState<number[]>(
        D.perRingObjectSvgIndices,
    );
    const [perSectorObjectSvgIndices, setPerSectorObjectSvgIndices] = useState<number[]>(
        D.perSectorObjectSvgIndices,
    );
    const [uniformShape, setUniformShape] = useState<CellObjectType>(
        D.uniformShape,
    );
    const [perRingShapes, setPerRingShapes] = useState<CellObjectType[]>(
        D.perRingShapes,
    );
    const [perSectorShapes, setPerSectorShapes] = useState<CellObjectType[]>(
        D.perSectorShapes,
    );

    const [sizeMode, setSizeMode] = useState<ScalarMode>(D.sizeMode);
    const [uniformSize, setUniformSize] = useState(D.uniformSize);
    const [sizeMin, setSizeMin] = useState(D.sizeMin);
    const [sizeMax, setSizeMax] = useState(D.sizeMax);

    const [opacityMode, setOpacityMode] = useState<ScalarMode>(D.opacityMode);
    const [uniformOpacity, setUniformOpacity] = useState(D.uniformOpacity);
    const [opacityMin, setOpacityMin] = useState(D.opacityMin);
    const [opacityMax, setOpacityMax] = useState(D.opacityMax);

    const [fill, setFill] = useState(D.fill);
    const [rotateWithCell, setRotateWithCell] = useState(D.rotateWithCell);
    const [useCustomSvg, setUseCustomSvg] = useState(D.useCustomSvg);
    const [svgSymbol, setSvgSymbol] = useState(D.svgSymbol);
    const [objectSvgs, setObjectSvgs] = useState<PolarObjectSvg[]>(() => {
        const squarePreset = OBJECT_SVG_PRESETS.find((p) => p.id === "square");
        if (!squarePreset || !isValidSvg(squarePreset.svg)) return [];
        return [{ name: `${squarePreset.label} (preset)`, content: squarePreset.svg }];
    });
    const [seed, setSeed] = useState(D.seed);

    const setRings = useCallback((v: number) => {
        const next = Math.max(1, Math.round(v));
        setRingsState(next);
        setRingConfigs((prev) => normalizeRingConfigs(next, prev, D.sectors));
    }, []);

    const setRingSectors = useCallback((ringIdx: number, value: number) => {
        setRingConfigs((prev) =>
            prev.map((rc, i) =>
                i === ringIdx
                    ? { ...rc, sectors: Math.max(1, Math.round(value)) }
                    : rc,
            ),
        );
    }, []);

    const setRingRotationDeg = useCallback((ringIdx: number, value: number) => {
        setRingConfigs((prev) =>
            prev.map((rc, i) =>
                i === ringIdx
                    ? { ...rc, rotationDeg: Number.isFinite(value) ? value : 0 }
                    : rc,
            ),
        );
    }, []);

    const setRingHeightWeight = useCallback(
        (ringIdx: number, value: number) => {
            setRingConfigs((prev) =>
                prev.map((rc, i) =>
                    i === ringIdx
                        ? { ...rc, heightWeight: Math.max(0.01, value) }
                        : rc,
                ),
            );
        },
        [],
    );

    const setRingCellHeightFactor = useCallback(
        (ringIdx: number, value: number) => {
            setRingConfigs((prev) =>
                prev.map((rc, i) =>
                    i === ringIdx
                        ? {
                              ...rc,
                              cellHeightFactor: Math.max(0, Math.min(3, value)),
                          }
                        : rc,
                ),
            );
        },
        [],
    );

    const setRingPopulationRule = useCallback(
        (ringIdx: number, rule: RingPopulationRule) => {
            setRingConfigs((prev) =>
                prev.map((rc, i) =>
                    i === ringIdx ? { ...rc, populationRule: rule } : rc,
                ),
            );
        },
        [],
    );

    const addObjectSvg = useCallback((svg: PolarObjectSvg) => {
        if (!svg.content || !isValidSvg(svg.content)) return;
        setObjectSvgs((prev) => [...prev, svg]);
    }, []);

    const removeObjectSvg = useCallback((index: number) => {
        setObjectSvgs((prev) => prev.filter((_, i) => i !== index));
        setRingConfigs((prev) =>
            prev.map((rc) => {
                const cur = rc.objectSvgIndex ?? -1;
                if (cur === index) return { ...rc, objectSvgIndex: -1 };
                if (cur > index) return { ...rc, objectSvgIndex: cur - 1 };
                return rc;
            }),
        );
        setUniformObjectSvgIndex((prev) =>
            prev === index ? -1 : prev > index ? prev - 1 : prev,
        );
        setPerRingObjectSvgIndices((prev) =>
            prev.map((v) => (v === index ? -1 : v > index ? v - 1 : v)),
        );
        setPerSectorObjectSvgIndices((prev) =>
            prev.map((v) => (v === index ? -1 : v > index ? v - 1 : v)),
        );
    }, []);

    const setRingObjectSvgIndex = useCallback((ringIdx: number, objectIdx: number) => {
        setRingConfigs((prev) =>
            prev.map((rc, i) =>
                i === ringIdx
                    ? { ...rc, objectSvgIndex: Math.max(-1, Math.floor(objectIdx)) }
                    : rc,
            ),
        );
    }, []);

    // ── Per-cell shape array setters ──────────────────────────────────────

    const setPerRingShape = useCallback(
        (ringIdx: number, shape: CellObjectType) => {
            setPerRingShapes((prev) => {
                const next = [...prev];
                // grow array if needed
                while (next.length <= ringIdx) next.push(D.uniformShape);
                next[ringIdx] = shape;
                return next;
            });
        },
        [],
    );

    const setPerSectorShape = useCallback(
        (sectorIdx: number, shape: CellObjectType) => {
            setPerSectorShapes((prev) => {
                const next = [...prev];
                while (next.length <= sectorIdx) next.push(D.uniformShape);
                next[sectorIdx] = shape;
                return next;
            });
        },
        [],
    );

    const setPerRingObjectSvgIndex = useCallback((ringIdx: number, idx: number) => {
        setPerRingObjectSvgIndices((prev) => {
            const next = [...prev];
            while (next.length <= ringIdx) next.push(-1);
            next[ringIdx] = Math.max(-1, Math.floor(idx));
            return next;
        });
    }, []);

    const setPerSectorObjectSvgIndex = useCallback(
        (sectorIdx: number, idx: number) => {
            setPerSectorObjectSvgIndices((prev) => {
                const next = [...prev];
                while (next.length <= sectorIdx) next.push(-1);
                next[sectorIdx] = Math.max(-1, Math.floor(idx));
                return next;
            });
        },
        [],
    );

    // ── SVG generation ────────────────────────────────────────────────────

    const gridConfig = useMemo<PolarGridCellsConfig>(
        () => ({
            rings,
            sectors,
            ringConfigs,
            innerRadiusFraction,
            cellPadding,
            shapeMode,
            uniformObjectSvgIndex,
            perRingObjectSvgIndices,
            perSectorObjectSvgIndices,
            uniformShape,
            perRingShapes,
            perSectorShapes,
            sizeMode,
            uniformSize,
            sizeMin,
            sizeMax,
            opacityMode,
            uniformOpacity,
            opacityMin,
            opacityMax,
            fill,
            rotateWithCell,
            useCustomSvg,
            svgSymbol,
            objectSvgs,
            seed,
            canvasSize,
        }),
        [
            rings,
            sectors,
            ringConfigs,
            innerRadiusFraction,
            cellPadding,
            shapeMode,
            uniformObjectSvgIndex,
            perRingObjectSvgIndices,
            perSectorObjectSvgIndices,
            uniformShape,
            perRingShapes,
            perSectorShapes,
            sizeMode,
            uniformSize,
            sizeMin,
            sizeMax,
            opacityMode,
            uniformOpacity,
            opacityMin,
            opacityMax,
            fill,
            rotateWithCell,
            useCustomSvg,
            svgSymbol,
            objectSvgs,
            seed,
            canvasSize,
        ],
    );

    const svgContent = useMemo(
        () => buildPolarGridCellsSvg(gridConfig),
        [gridConfig],
    );

    // ── Download ──────────────────────────────────────────────────────────

    const handleDownload = useCallback(() => {
        const blob = new Blob([svgContent], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "polar-grid-cells.svg";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [svgContent]);

    // ── Presets ───────────────────────────────────────────────────────────

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            rings,
            sectors,
            innerRadiusFraction,
            cellPadding,
            canvasSize,
            ringConfigs,
            shapeMode,
            uniformObjectSvgIndex,
            perRingObjectSvgIndices,
            perSectorObjectSvgIndices,
            uniformShape,
            perRingShapes,
            perSectorShapes,
            sizeMode,
            uniformSize,
            sizeMin,
            sizeMax,
            opacityMode,
            uniformOpacity,
            opacityMin,
            opacityMax,
            fill,
            rotateWithCell,
            useCustomSvg,
            svgSymbol,
            objectSvgs,
            seed,
        }),
        [
            rings,
            sectors,
            innerRadiusFraction,
            cellPadding,
            canvasSize,
            ringConfigs,
            shapeMode,
            uniformObjectSvgIndex,
            perRingObjectSvgIndices,
            perSectorObjectSvgIndices,
            uniformShape,
            perRingShapes,
            perSectorShapes,
            sizeMode,
            uniformSize,
            sizeMin,
            sizeMax,
            opacityMode,
            uniformOpacity,
            opacityMin,
            opacityMax,
            fill,
            rotateWithCell,
            useCustomSvg,
            svgSymbol,
            objectSvgs,
            seed,
        ],
    );

    const applyPreset = useCallback(
        (values: Record<string, unknown>) => {
            if (values.rings != null) setRings(values.rings as number);
            if (values.sectors != null) {
                const upgraded = normalizeRingConfigs(
                    values.rings != null ? (values.rings as number) : rings,
                    ringConfigs,
                    values.sectors as number,
                );
                setRingConfigs(upgraded);
            }
            if (values.ringConfigs != null) {
                setRingConfigs(
                    normalizeRingConfigs(
                        values.rings != null ? (values.rings as number) : rings,
                        values.ringConfigs as RingConfig[],
                        sectors,
                    ),
                );
            }
            if (values.innerRadiusFraction != null)
                setInnerRadiusFraction(values.innerRadiusFraction as number);
            if (values.cellPadding != null)
                setCellPadding(values.cellPadding as number);
            if (values.canvasSize != null)
                setCanvasSize(values.canvasSize as number);
            if (values.shapeMode != null)
                setShapeMode(values.shapeMode as ShapeMode);
            if (values.uniformObjectSvgIndex != null)
                setUniformObjectSvgIndex(values.uniformObjectSvgIndex as number);
            if (values.perRingObjectSvgIndices != null)
                setPerRingObjectSvgIndices(values.perRingObjectSvgIndices as number[]);
            if (values.perSectorObjectSvgIndices != null)
                setPerSectorObjectSvgIndices(
                    values.perSectorObjectSvgIndices as number[],
                );
            if (values.uniformShape != null)
                setUniformShape(values.uniformShape as CellObjectType);
            if (values.perRingShapes != null)
                setPerRingShapes(values.perRingShapes as CellObjectType[]);
            if (values.perSectorShapes != null)
                setPerSectorShapes(values.perSectorShapes as CellObjectType[]);
            if (values.sizeMode != null)
                setSizeMode(values.sizeMode as ScalarMode);
            if (values.uniformSize != null)
                setUniformSize(values.uniformSize as number);
            if (values.sizeMin != null) setSizeMin(values.sizeMin as number);
            if (values.sizeMax != null) setSizeMax(values.sizeMax as number);
            if (values.opacityMode != null)
                setOpacityMode(values.opacityMode as ScalarMode);
            if (values.uniformOpacity != null)
                setUniformOpacity(values.uniformOpacity as number);
            if (values.opacityMin != null)
                setOpacityMin(values.opacityMin as number);
            if (values.opacityMax != null)
                setOpacityMax(values.opacityMax as number);
            if (values.fill != null) setFill(values.fill as string);
            if (values.rotateWithCell != null)
                setRotateWithCell(values.rotateWithCell as boolean);
            if (values.useCustomSvg != null)
                setUseCustomSvg(values.useCustomSvg as boolean);
            if (values.svgSymbol != null)
                setSvgSymbol(values.svgSymbol as string);
            if (values.objectSvgs != null)
                setObjectSvgs(values.objectSvgs as PolarObjectSvg[]);
            if (values.seed != null) setSeed(values.seed as number);
        },
        [ringConfigs, rings, sectors, setRings],
    );

    const randomizeParameters = useCallback(() => {
        const nextRings = Math.floor(Math.random() * 8) + 3; // 3-10
        setRings(nextRings);
        setRingConfigs(
            Array.from({ length: nextRings }, () => {
                const ruleRoll = Math.random();
                const populationRule =
                    ruleRoll < 0.25
                        ? ({ type: "all" } as RingPopulationRule)
                        : ruleRoll < 0.5
                          ? ({
                                type: "randomDensity",
                                density: Math.random(),
                                seedOffset: Math.floor(Math.random() * 64),
                            } as RingPopulationRule)
                          : ruleRoll < 0.75
                            ? ({
                                  type: "everyN",
                                  step: Math.floor(Math.random() * 8) + 1,
                                  phase: Math.floor(Math.random() * 8),
                              } as RingPopulationRule)
                            : ({
                                  type: "fillNSkipN",
                                  fill: Math.floor(Math.random() * 6) + 1,
                                  skip: Math.floor(Math.random() * 6),
                                  phase: Math.floor(Math.random() * 8),
                              } as RingPopulationRule);
                return {
                    sectors: Math.floor(Math.random() * 30) + 6,
                    rotationDeg: Math.floor(Math.random() * 360) - 180,
                    heightWeight: +(Math.random() * 2.7 + 0.3).toFixed(2),
                    cellHeightFactor: +(Math.random() * 2.8 + 0.2).toFixed(2),
                    populationRule,
                    objectSvgIndex:
                        objectSvgs.length > 0
                            ? Math.floor(Math.random() * (objectSvgs.length + 1)) - 1
                            : -1,
                };
            }),
        );
        const shapeModes: ShapeMode[] = [
            "uniform",
            "per-ring",
            "per-sector",
            "random",
        ];
        const scalarModes: ScalarMode[] = [
            "uniform",
            "radial-in",
            "radial-out",
            "random",
        ];
        const pick = <T,>(arr: T[]): T =>
            arr[Math.floor(Math.random() * arr.length)];
        setShapeMode(pick(shapeModes));
        setUniformObjectSvgIndex(
            objectSvgs.length > 0 ? Math.floor(Math.random() * objectSvgs.length) : -1,
        );
        setUniformShape(pick(CELL_OBJECT_TYPES));
        setSizeMode(pick(scalarModes));
        setUniformSize(+(Math.random() * 0.8 + 0.15).toFixed(2));
        setSizeMin(+(Math.random() * 0.55 + 0.05).toFixed(2));
        setSizeMax(+(Math.random() * 0.45 + 0.5).toFixed(2));
        setOpacityMode(pick(scalarModes));
        setUniformOpacity(+(Math.random() * 0.7 + 0.3).toFixed(2));
        setOpacityMin(+(Math.random() * 0.5 + 0.05).toFixed(2));
        setOpacityMax(1);
        setInnerRadiusFraction(+(Math.random() * 0.7).toFixed(2));
        setCellPadding(+(Math.random() * 0.45).toFixed(2));
        setRotateWithCell(Math.random() < 0.5);
        setSeed(Math.floor(Math.random() * 0x1_0000_0000));
    }, [objectSvgs.length, setRings]);

    const { resetParameters } = useToolParamPersistence({
        toolId: "polar-grid-cells",
        defaultPreset: DEFAULT_POLAR_GRID_CELLS_CONFIG as unknown as Record<
            string,
            unknown
        >,
        getPresetValues,
        applyPreset,
    });

    const value: PolarGridCellsState = {
        rings,
        ringConfigs,
        innerRadiusFraction,
        cellPadding,
        canvasSize,
        shapeMode,
        uniformObjectSvgIndex,
        perRingObjectSvgIndices,
        perSectorObjectSvgIndices,
        uniformShape,
        perRingShapes,
        perSectorShapes,
        sizeMode,
        uniformSize,
        sizeMin,
        sizeMax,
        opacityMode,
        uniformOpacity,
        opacityMin,
        opacityMax,
        fill,
        rotateWithCell,
        useCustomSvg,
        svgSymbol,
        objectSvgs,
        seed,
        svgContent,
        setRings,
        setRingSectors,
        setRingRotationDeg,
        setRingHeightWeight,
        setRingCellHeightFactor,
        setRingPopulationRule,
        setInnerRadiusFraction,
        setCellPadding,
        setCanvasSize,
        setShapeMode,
        setUniformObjectSvgIndex,
        setPerRingObjectSvgIndex,
        setPerSectorObjectSvgIndex,
        setUniformShape,
        setPerRingShape,
        setPerSectorShape,
        setSizeMode,
        setUniformSize,
        setSizeMin,
        setSizeMax,
        setOpacityMode,
        setUniformOpacity,
        setOpacityMin,
        setOpacityMax,
        setFill,
        setRotateWithCell,
        setUseCustomSvg,
        setSvgSymbol,
        addObjectSvg,
        removeObjectSvg,
        setRingObjectSvgIndex,
        setSeed,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
    };

    return (
        <PolarGridCellsContext.Provider value={value}>
            {children}
        </PolarGridCellsContext.Provider>
    );
}

// Re-export for convenience
export { CELL_OBJECT_TYPES };
